"use client";

import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { TriangleAlert } from "lucide-react";

// Shared UI for the route-level error boundaries (app/(root)/error.tsx and
// app/(dashboard)/dashboard/error.tsx) and for app/global-error.tsx. Two jobs:
//   1. Recover gracefully — show a friendly "try again" view instead of the raw
//      "Application error: a client-side exception has occurred" white screen.
//   2. Instrument — console.error the real error (with stack). With
//      productionBrowserSourceMaps enabled (next.config.ts), the logged stack
//      resolves to real source on the next occurrence, so any crash names the
//      offending component instead of a minified chunk offset.

// Throttle key for the chunk-error auto-reload. A timestamp (not a boolean) so
// we never get permanently stuck: we only suppress a re-reload if one happened
// in the last few seconds (i.e. the reload didn't help) — a fresh chunk error
// later is still allowed to reload.
const CHUNK_RELOAD_KEY = "loyeecards-chunk-reload-at";
const CHUNK_RELOAD_WINDOW_MS = 10_000;

function isChunkLoadError(error: Error) {
  return (
    error?.name === "ChunkLoadError" ||
    /Loading chunk|Loading CSS chunk|dynamically imported module|Failed to fetch dynamically imported/i.test(
      error?.message ?? "",
    )
  );
}

export default function ErrorFallback({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error("Caught by error boundary:", error);

    // A stale/missing JS chunk (common right after a deploy, or a flaky fetch
    // during a client-side navigation) self-heals with a fresh load. Reload
    // once — guarded so we never loop if the chunk is genuinely gone.
    if (isChunkLoadError(error)) {
      try {
        const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
        if (Date.now() - last > CHUNK_RELOAD_WINDOW_MS) {
          sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
          window.location.reload();
        }
      } catch {
        // sessionStorage unavailable — fall through to the manual fallback.
      }
    }
  }, [error]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
      <TriangleAlert className="size-10 text-destructive" />
      <h1 className="text-2xl font-bold">Something went wrong</h1>
      <p className="max-w-sm text-sm text-muted-foreground">
        Sorry — something hiccuped while loading the page. This is usually
        temporary. Please try again.
      </p>
      <div className="flex gap-2">
        <Button onClick={() => reset()}>Try again</Button>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </div>
    </div>
  );
}
